import React, { useState } from 'react';
import { Checkbox, Modal, message } from 'antd';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import iconClose from 'utils/icon/icon_close_circle.svg';
import { handleMessage, showApiErrorMessage } from 'utils/helper/message';
import PrimaryButton from 'components/button/PrimaryButton';

import { costumeApis } from 'apis/resource/costumeApis';
import SelectBox from 'components/SelectBox';
import InputStyled from 'components/InputStyled';
import { ICommonCode } from 'utils/types/storyTypes';

/**
 * * 리소스 - 미니컷 등록 모달
 */

interface IProps {
  visible: {
    register: boolean;
    delete: boolean;
    update: boolean;
    name: string;
  };
  setVisible: (e: any) => void;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: boolean;
  projectId: string | undefined;
  charactersData: ICommonCode[] | null;
  data: any;
  setData: React.Dispatch<any>;
  modelsData: ICommonCode[] | null;
}

const CostumeUpdateModal = ({
  visible,
  setVisible,
  setRefresh,
  refresh,
  projectId,
  charactersData,
  data,
  setData,
  modelsData,
}: IProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  /// 서버 전송
  const handleSubmit = async () => {
    if (projectId && data) {
      try {
        setLoading(true);
        await costumeApis.costumeUpdate(projectId, data.dress_id, {
          update: data,
        });

        console.log(`costumeUpdate done`);

        setRefresh(!refresh);
        setLoading(false);
        handleClose();
      } catch (err) {
        console.log(`costumeUpdate catch exception`);

        setLoading(false);

        showApiErrorMessage(err);
      }
    }
  };

  const handleClose = () => {
    setVisible({ ...visible, update: false });
  };

  const handleInputChange = (value, type: string) => {
    const cloneData = JSON.parse(JSON.stringify(data));
    cloneData[type] = value;
    setData(cloneData);
  };

  const handleCheckboxChange = (value, type: string) => {
    const cloneData = JSON.parse(JSON.stringify(data));
    cloneData[type] = value;
    setData(cloneData);
  };

  return (
    <CustomModal
      title={t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.editModal.title',
      )}
      open={visible.update}
      onCancel={handleClose}
      footer={
        <ButtonWrapper>
          <PrimaryButton
            text={t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.editModal.button',
            )}
            height='40px'
            width='fit-content'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </ButtonWrapper>
      }
      closeIcon={
        <img src={iconClose} width={20} height={20} alt='icon-close' />
      }
    >
      <SectionWarpper>
        <div>
          <HeaderWrapper>{data && <p>ID: {data.dress_id}</p>}</HeaderWrapper>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.editModal.name',
            )}
          </p>
          <InputStyled
            value={data?.dress_name}
            onChange={(e) => {
              handleInputChange(e.target.value, 'dress_name');
            }}
            maxLength={30}
          />
          <p className='length-subtext'>
            {typeof data?.dress_name.length === 'number'
              ? `${data?.dress_name.length}/30 characters`
              : '0/30 characters'}
          </p>
        </div>
        <div>
          <p className='title'>프로필 캐릭터</p>
          <SelectBox
            value={data?.speaker}
            onChange={(e) => {
              handleInputChange(e, 'speaker');
            }}
            options={charactersData?.map((v, i) => {
              return {
                value: v.code,
                label: v.code_name,
              };
            })}
          />
        </div>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.editModal.model',
            )}
          </p>
          <SelectBox
            value={data?.model_id}
            onChange={(e) => {
              handleInputChange(e, 'model_id');
            }}
            options={modelsData?.map((v, i) => {
              return {
                value: v.code,
                label: v.code_name,
              };
            })}
          />
        </div>
        <CheckboxWrapper>
          <p>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.costumeContent.editModal.default',
            )}
            :
          </p>
          <Checkbox
            checked={data?.is_default}
            onChange={(e) => {
              handleCheckboxChange(e.target.checked, 'is_default');
            }}
          />
        </CheckboxWrapper>
      </SectionWarpper>
    </CustomModal>
  );
};

export default CostumeUpdateModal;

const CustomModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-content {
    box-shadow: none;
    width: 566px;
  }
  .ant-radio-wrapper {
    span {
      font-size: ${theme.fontSizes.normal};
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.2px;
      color: ${theme.colors.dark_500};
    }
  }
  .ant-modal-footer {
    margin-top: 10px !important;
  }
  ul {
    margin-left: 25px;
  }
  .ant-modal-close:hover {
    background-color: transparent;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
`;

const SectionWarpper = styled.div`
  & > * + * {
    margin-top: 8px;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  & > * + * {
    margin-left: 1rem;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  p {
    color: ${theme.colors.dark_500};
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.md};
  }
`;
